import React from "react";
import { Plane, Hotel, Car, ParkingCircle, Compass, MapPin } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";

const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=" +
  encodeURIComponent("The Metropolitan Club, 5895 Windward Parkway, Alpharetta, GA 30005");

export default function Travel() {
  const { content } = useContent();
  const t = content?.travel;
  if (!t) return null;

  const blocks = [
    { icon: Plane, title: "Airport", body: t.airport },
    { icon: Car, title: "Transportation", body: t.transportation },
    { icon: ParkingCircle, title: "Parking", body: t.parking },
    { icon: Compass, title: "Things To Do", body: t.things_to_do },
  ];

  return (
    <div data-testid="travel-page">
      <PageHero overline="Getting Here" title="Travel & Stay" subtitle="Everything you need to plan your trip to Alpharetta." />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 space-y-16">
        {/* venue address + map link */}
        <Reveal>
          <div className="stone-panel border border-taupe/40 rounded-lg p-10 text-center">
            <MapPin className="mx-auto text-olive" size={26} />
            <h2 className="font-serif text-3xl mt-3 text-matte">The Metropolitan Club</h2>
            <p className="text-taupe mt-2">5895 Windward Parkway, Alpharetta, Georgia 30005</p>
            <a href={MAPS_URL} target="_blank" rel="noopener noreferrer"
              className="btn-outline rounded-full px-7 py-3 overline inline-block mt-6" data-testid="travel-map-btn">
              View on Google Maps
            </a>
          </div>
        </Reveal>

        {/* hotels */}
        <Reveal>
          <div>
            <div className="flex items-center gap-3 mb-6"><Hotel className="text-olive" size={24} /><h2 className="font-serif text-4xl text-matte">Hotel Recommendations</h2></div>
            <div className="grid grid-cols-1 gap-6">
              {(t.hotels || []).map((h, i) => (
                <div key={i} className="bg-soft-white border border-taupe/40 rounded-lg p-8 w-full" data-testid={`hotel-${i}`}>
                  {i === 0 && <p className="overline text-olive">Host Hotel</p>}
                  <h3 className="font-serif text-2xl text-matte mt-1">{h.name}</h3>
                  <p className="text-taupe text-sm mt-2">{h.address}</p>
                  {h.note && <p className="text-matte/80 text-sm mt-3 leading-relaxed">{h.note}</p>}
                  {(h.code || h.deadline) && (
                    <dl className="mt-4 text-sm space-y-1 text-matte/80">
                      {h.code && <div><span className="text-taupe">Group Code: </span>{h.code}</div>}
                      {h.deadline && <div><span className="text-taupe">Book By: </span>{h.deadline}</div>}
                    </dl>
                  )}
                  {h.link && /^https?:\/\//.test(h.link) && (
                    <a href={h.link} target="_blank" rel="noopener noreferrer" className="btn-olive rounded-full px-6 py-2.5 overline inline-block mt-5">Book Now</a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* info blocks */}
        <div className="grid md:grid-cols-2 gap-6">
          {blocks.map((b, i) => (
            <Reveal key={b.title} delay={i * 80}>
              <div className="bg-soft-white border border-taupe/40 rounded-lg p-8 h-full">
                <b.icon className="text-olive" size={24} />
                <h3 className="font-serif text-2xl mt-3 text-matte">{b.title}</h3>
                <p className="text-taupe mt-2 leading-relaxed">{b.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
